import airbrake from "utils/airbrake"
import units from "utils/units"
import { WindIcon } from "components/wind-icon"
import initSwellIcons from "components/swell-icon"
import { SnowAmount } from "components/snow-amount"
import { RainAmount } from "components/rain-amount"
import { TempValue } from "components/temp-value" // Snow and Mtn use the cold scale
import { TideTime } from "components/tide-time"

import { LevelValue } from "./level-value"
import ForecastTableHeader from "./header"
import { KEY_GRAPH } from "./data"

export const BOUNDARY_DETAIL = 1
export const MORNING_START = 8
const NIGHT_START = 20

const currentHourClass = "forecast-table__time--current-hour"

function cellAtColumn(cells, column) {
  let end = 0

  return cells.find((cell) => {
    end += cell.colSpan
    return column < end
  })
}

function updateCurrentHourOutline(cells) {
  if (!cells.time || !cells.sunset) return

  const column = cells.time.findIndex((cell) => cell.querySelector(`.${currentHourClass}`))
  if (column < 0) return

  const currentHour = cells.time[column].querySelector(`.${currentHourClass}`)
  const sunset = cellAtColumn(cells.sunset, column)?.firstElementChild
  if (!currentHour?.isConnected || !sunset?.isConnected) return

  const height = sunset.getBoundingClientRect().bottom - currentHour.getBoundingClientRect().top
  currentHour.style.setProperty("--current-hour-height", `${height}px`)
}

function insertRowBefore(cells, name, before) {
  let debug = false
  try {
    if (localStorage.getItem("fc-debug")) debug = true
  } catch (e) {}

  const rowCells = cells[name]
  if (!rowCells || !Array.isArray(rowCells)) {
    throw new Error(`Missing or invalid row "${name}" in expansion data`)
  }

  const parent = before.parentNode
  if (debug) {
    console.log(cells)
    console.log(name)
  }
  rowCells.forEach((td) => parent.insertBefore(td, before))
}

function isHeader(name) {
  return /^days\d*$/.test(name)
}

export default class ForecastTableDay {
  constructor({ table, date, minDetail, summaryCell }) {
    this.table = table
    this.date = date
    this.minDetail = minDetail
    this.summaryCell = summaryCell
    this.cells = {}
    this.headers = {}
    this.mapColSpans = new WeakMap()
    this.loading = false
    this.size = 0
  }

  get expanded() {
    return this.detail > 0
  }

  get maxDetail() {
    return this.header.maxDetail
  }

  isIncomplete() {
    const cs = this.cellsFor({ detail: 0 })
    return !cs || !cs.time || cs.time.length < 3
  }

  expand() {
    const detail = Math.min(this.detail + 1, this.maxDetail)
    return this.changeDetail(detail)
  }

  collapse() {
    const detail = Math.max(this.minDetail, this.detail - 1)
    return this.changeDetail(detail)
  }

  insert(model, level, detail, cells, beforeDay) {
    this.setCells(model, level, detail, cells)
    this.insertCells(cells, beforeDay)
  }

  async changeDetail(detail) {
    if (detail === this.detail || this.loading) return

    this.loading = true

    try {
      await this.table.changeDay(this, detail)
    } catch (e) {
      airbrake.notify(e)
    } finally {
      this.loading = false
    }
  }

  columnsFor(model, level, detail) {
    const cells = this.cellsFor({ model, level, detail })
    if (!cells?.time) return []

    return cells.time.map((cell, index) => ({
      cell,
      detail,
      index,
      hour: Number.parseInt(cell.dataset.hour, 10),
    }))
  }

  hasEarlyNight({ model, level }) {
    const columns = this.columnsFor(model, level, BOUNDARY_DETAIL)
    return !!columns.length && columns.at(-1).hour >= MORNING_START - 3
  }

  async prepareState(model, level, previousExpanded, nextExpanded, nextEarlyNight) {
    let columns

    // before calling prepareStates caller should have setCells so if they are missing do nothing
    const cellsBoundary = this.cellsFor({ model, level, detail: BOUNDARY_DETAIL })
    if (this.expanded) {
      columns = this.columnsFor(model, level, this.detail)

      if (!nextEarlyNight) {
        const night = this.columnsFor(model, level, 0).slice(-1)
        columns = [...columns.filter(({ hour }) => hour < NIGHT_START), ...night]
      }
    } else if (!cellsBoundary) {
      columns = this.columnsFor(model, level, this.detail)
    } else {
      const base = this.columnsFor(model, level, 0)
      let detailed = []

      if (previousExpanded || nextExpanded) {
        detailed = this.columnsFor(model, level, BOUNDARY_DETAIL)
      }

      const detailedMorning = previousExpanded && this.hasEarlyNight({ model, level })
      const morning = detailedMorning ? detailed.filter(({ hour }) => hour < MORNING_START) : []
      const middle = nextExpanded ? base.slice(0, -1) : base
      const night = nextExpanded ? detailed.filter(({ hour }) => hour >= NIGHT_START) : []
      columns = [...morning, ...middle, ...night]
    }

    Object.keys(this.refCells).forEach((name) => {
      if (name === KEY_GRAPH || name === "maps" || isHeader(name)) return

      columns.forEach((column) => {
        const cells = this.cellsFor({ model, level, detail: column.detail })
        if (!cells?.[name]?.[column.index]) {
          throw new Error(
            `Missing row "${name}" column ${column.detail}/${column.index} for ${this.date}`,
          )
        }
      })
    })

    return columns
  }

  applyState(columns, model, level) {
    const unchanged =
      this.visibleColumns.length === columns.length &&
      this.visibleColumns.every((column, index) => {
        const other = columns[index]
        return column.cell === other.cell
      })
    if (unchanged) return

    const oldSize = this.size
    const newSize = columns.length
    const freezingData = this.freezingDataFor(columns, model, level)

    Object.keys(this.refCells).forEach((name) => {
      if (name === KEY_GRAPH) return

      if (isHeader(name)) {
        this.renderHeader(name, newSize, model, level)
      } else if (name === "maps") {
        this.renderMaps(columns, model, level)
      } else {
        const cells = columns.map(
          (column) => this.cellsFor({ model, level, detail: column.detail })[name][column.index],
        )
        this.replaceRowCells(name, cells)
      }
    })

    if (this.summaryCell) this.summaryCell.colSpan += newSize - oldSize

    this.table.dayChanged(this, oldSize, newSize, freezingData)
    this.visibleColumns = columns
    this.size = newSize
    updateCurrentHourOutline(this.rowCells)
  }

  renderHeader(name, size, model, level) {
    const header = this.headers[name]
    if (!header) return

    const source = this.cellsFor({ model, level })?.[name]?.[0]
    if (source) header.update(this.detail, size, source)
    else header.updateExpand(this.detail, size)

    this.rowCells[name] = [header.root]
  }

  renderMaps(columns, model, level) {
    let cells

    if (this.expanded) {
      const sourceCells = [...this.cellsFor({ model, level }).maps]
      sourceCells.forEach((_, index) => {
        const cell = sourceCells[index]
        cell.colSpan = this.mapColSpans.get(cell) || cell.colSpan
      })

      // a map image can cover more of the day than is actually shown once the night
      // columns collapse into a single boundary column (see prepareState); clamp
      // each cell so the row's total width never exceeds the day's
      let remaining = columns.length

      cells = sourceCells.filter((_, index) => {
        if (remaining <= 0) return false

        const cell = sourceCells[index]
        cell.colSpan = Math.min(cell.colSpan, remaining)
        remaining -= cell.colSpan
        return true
      })

      // The template adds --border only when a map begins on the day's last
      // period. The last map of an expanded day usually begins earlier and
      // spans that period, so it never gets the class and is 1px too narrow.
      this.applyExpandedMapBorder(cells, remaining, columns)
    } else {
      const baseCells = [...this.cellsFor({ model, level, detail: 0 }).maps]
      const ranges = []
      let end = 0

      baseCells.forEach((cell) => {
        end += this.mapColSpans.get(cell) || cell.colSpan
        ranges.push(end)
      })

      const counts = baseCells.map(() => 0)
      columns.forEach((column) => {
        let mapIndex
        if (column.detail === 0) {
          mapIndex = ranges.findIndex((rangeEnd) => column.index < rangeEnd)
        } else {
          mapIndex = column.hour < MORNING_START ? 0 : baseCells.length - 1
        }
        counts[Math.max(mapIndex, 0)]++
      })

      cells = baseCells.filter((_, index) => {
        baseCells[index].colSpan = counts[index]
        return counts[index] > 0
      })
    }

    this.replaceRowCells("maps", cells)
  }

  applyExpandedMapBorder(cells, remaining, columns) {
    // The last period's time cell has the day-end border.
    const dayHasBorder = columns.at(-1)?.cell.querySelector(".forecast-table__container--border")
    // 'remaining' is how many periods no map covers. 0 means the last map
    // reaches the day-end border, so that map is the one to widen by 1px.
    const endingCell = remaining === 0 && dayHasBorder ? cells.at(-1) : null

    // Only remove a class this function added. A later expand can move the
    // day edge onto another map, and the previous one would stay 1px too wide.
    if (this.widenedMapCell && this.widenedMapCell !== endingCell) {
      this.widenedMapCell
        .querySelector(".forecast-table__maps")
        ?.classList.remove("forecast-table__maps--border", "forecast-table__container--border")
      this.widenedMapCell = null
    }

    if (!endingCell) return

    const maps = endingCell.querySelector(".forecast-table__maps")
    // Already present means the template added it. Leave it untracked so a
    // later expand does not strip that border.
    if (!maps || maps.classList.contains("forecast-table__maps--border")) return

    maps.classList.add("forecast-table__maps--border", "forecast-table__container--border")
    this.widenedMapCell = endingCell
  }

  replaceRowCells(name, cells) {
    const current = this.rowCells[name]
    const attached = current.filter((cell) => cell.parentNode)
    if (!attached.length) throw new Error(`No attached row "${name}" cells for ${this.date}`)

    const row = attached[0].parentNode
    const before = attached.at(-1).nextSibling
    current.forEach((cell) => cell.remove())
    cells.forEach((cell) => row.insertBefore(cell, before))
    this.rowCells[name] = cells
  }

  freezingDataFor(columns, model, level) {
    if (!this.refCells[KEY_GRAPH]) return undefined

    const first = this.cellsFor({ model, level, detail: columns[0]?.detail })?.[KEY_GRAPH]
    if (!first) throw new Error(`Missing freezing graph data for ${this.date}`)

    const groups = first[0].map(() => [])
    const points = first[1].map(() => [])

    columns.forEach((column) => {
      const graph = this.cellsFor({ model, level, detail: column.detail })?.[KEY_GRAPH]
      if (!graph) throw new Error(`Missing freezing graph data for ${this.date}/${column.detail}`)

      graph[0].forEach((values, index) => groups[index].push(values[column.index]))
      graph[1].forEach((values, index) => points[index].push(values[column.index]))
    })

    return [groups, points]
  }

  setCells(model, level, detail, cells) {
    if (!cells) return

    const dayHeaders = {}

    Object.keys(cells).forEach((key) => {
      if (key === KEY_GRAPH) return

      if (isHeader(key) && key !== "days") {
        dayHeaders[key] = cells[key].at(0)
        return
      }

      cells[key].forEach((td, index) => {
        if (key === "maps" && !this.mapColSpans.has(td)) {
          this.mapColSpans.set(td, td.colSpan)
        }

        // TODO: replace with proper inheritance
        const initialized =
          window.initFCTableCell &&
          window.initFCTableCell(this, td, { model, level, detail, key, index })
        if (initialized) return

        WindIcon.init(td)
        initSwellIcons(td, units)
        SnowAmount.init(td)
        RainAmount.init(td)
        TempValue.init(td)
        TideTime.init(td)
        LevelValue.init(td)
      })
    })

    this.setCellsFor(model, level, detail, cells)

    let css
    if (detail === 2) css = "is-expanded-h"
    if (detail === 1) css = "is-expanded-t"

    if (css && detail > this.minDetail) {
      Object.keys(cells).forEach((key) => {
        if (key !== KEY_GRAPH) cells[key].forEach((cell) => cell.classList.add(css))
      })
    }

    if (this.detail === undefined) {
      this.rowCells = {}
      Object.keys(cells).forEach((name) => {
        if (name !== KEY_GRAPH) this.rowCells[name] = [...cells[name]]
      })

      this.refCells = cells
      this.detail = detail
      this.visibleColumns = this.columnsFor(model, level, detail)
      const header = cells.days[0]
      this.header = new ForecastTableHeader(header, this)
      this.headers.days = this.header
      this.size = cells.time.length

      Object.keys(dayHeaders).forEach((key) => {
        this.headers[key] = new ForecastTableHeader(dayHeaders[key], this)
      })
    }

    updateCurrentHourOutline(this.rowCells)
  }

  insertCells(cells, beforeDay) {
    if (!cells?.time) return

    const newSize = cells.time.length

    Object.keys(cells).forEach((name) => {
      if (name === KEY_GRAPH) return
      if (!Object.hasOwnProperty.call(cells, name)) return

      const before = beforeDay.firstCell(name)
      insertRowBefore(cells, name, before)
    })

    const freezingData = cells[KEY_GRAPH]
    this.size = 0
    this.table.dayChanged(this, 0, newSize, freezingData)
    this.size = newSize
  }

  cellsFor({ model, level, detail } = {}) {
    const m = model ?? this.table.model
    const l = level ?? this.table.level
    const d = detail ?? this.detail
    if (!this.cells[m]) this.cells[m] = {}
    if (!this.cells[m][l]) this.cells[m][l] = []
    return this.cells[m][l][d]
  }

  setCellsFor(model, level, detail, cells) {
    this.cellsFor({ model, level, detail })
    this.cells[model][level][detail] = cells
  }

  firstCell(name) {
    if (isHeader(name)) return this.headers[name].root
    return this.rowCells[name][0]
  }
}
